import { sessionRetentionUnits } from './session-retention.js';
import { SessionCapacityError, SESSION_MAX_BYTES, sessionKey, sessionRecordBytes, sessionTurnKey } from './journal.js';
export { SessionCapacityError, SESSION_MAX_BYTES, sessionKey, sessionRecordBytes, sessionTurnKey, isDirectMessageSessionScope } from './journal.js';
function request(value) {
    return new Promise((resolve, reject) => { value.onsuccess = () => resolve(value.result); value.onerror = () => reject(value.error); });
}
function completed(transaction) {
    return new Promise((resolve, reject) => { transaction.oncomplete = () => resolve(); transaction.onabort = () => reject(transaction.error ?? new Error('Local journal transaction aborted.')); transaction.onerror = () => reject(transaction.error); });
}
/** IndexedDB serializes append and eviction across tabs; no writer replaces another writer's snapshot. */
export class IndexedDbSessionJournal {
    options;
    database;
    maxBytes;
    constructor(options = {}) {
        this.options = options;
        this.maxBytes = options.maxBytes ?? SESSION_MAX_BYTES;
    }
    open() {
        if (this.database)
            return this.database;
        this.database = new Promise((resolve, reject) => {
            const factory = this.options.factory ?? globalThis.indexedDB;
            if (!factory) {
                reject(new Error('Local storage is unavailable.'));
                return;
            }
            const opening = factory.open(this.options.name ?? 'borgee-dsh-session-events', 1);
            opening.onupgradeneeded = () => {
                const database = opening.result;
                database.createObjectStore('sessions', { keyPath: 'key' });
                database.createObjectStore('events', { keyPath: 'id' }).createIndex('sessionKey', 'sessionKey');
            };
            opening.onsuccess = () => { opening.result.onversionchange = () => opening.result.close(); resolve(opening.result); };
            opening.onerror = () => reject(opening.error);
            opening.onblocked = () => reject(new Error('Local storage is blocked by another tab.'));
        });
        this.database.catch(() => { this.database = undefined; });
        return this.database;
    }
    async read(scope) {
        const database = await this.open();
        const transaction = database.transaction(['sessions', 'events'], 'readonly');
        const done = completed(transaction);
        void done.catch(() => undefined);
        const key = sessionKey(scope);
        const [meta, rows] = await Promise.all([
            request(transaction.objectStore('sessions').get(key)),
            request(transaction.objectStore('events').index('sessionKey').getAll(key)),
        ]);
        await done;
        if (!meta && !rows.length && scope.bindingId && await this.migrateLegacyBinding(scope))
            return this.read(scope);
        const ordered = rows.sort((a, b) => a.order - b.order);
        let ordinal = 0;
        const records = [], cuts = [];
        for (const row of ordered) {
            if (row.cut)
                cuts.push({ ...row.cut, ordinal: ++ordinal, records: row.cut.records.map(record => ({ ...record, ordinal: ++ordinal })) });
            else
                records.push({ ...row.record, ordinal: ++ordinal });
        }
        return { records, cuts, epochs: meta?.epochs, trimmed: meta?.trimmed ?? false };
    }
    /** Only explicit saved binding witnesses can move legacy root history into a selected Session. */
    async migrateLegacyBinding(scope) {
        const database = await this.open();
        const transaction = database.transaction(['sessions', 'events'], 'readwrite');
        const done = completed(transaction);
        void done.catch(() => undefined);
        try {
            const sessions = transaction.objectStore('sessions'), events = transaction.objectStore('events');
            const key = sessionKey(scope), legacyKey = sessionKey({ ...scope, bindingId: undefined });
            const [current, legacy, rows] = await Promise.all([
                request(sessions.get(key)), request(sessions.get(legacyKey)),
                request(events.index('sessionKey').getAll(legacyKey)),
            ]);
            if (current || !legacy) {
                await done;
                return !!current;
            }
            const witnesses = new Map();
            for (const row of rows) {
                const record = row.record, binding = record?.frame.session_control_binding_id;
                if (!record || !binding || !['runtime_updated', 'agent_snapshot'].includes(record.frame.event.type))
                    continue;
                const values = witnesses.get(record.generation) ?? new Set();
                values.add(binding);
                witnesses.set(record.generation, values);
            }
            const generations = new Set([...witnesses].filter(([, values]) => values.size === 1 && values.has(scope.bindingId)).map(([generation]) => generation));
            const moved = rows.filter(row => generations.has(row.cut?.generation ?? row.record.generation));
            if (!moved.length) {
                await done;
                return false;
            }
            const bytes = moved.reduce((sum, row) => sum + row.bytes, 0);
            for (const row of moved)
                events.put({ ...row, sessionKey: key });
            const pick = (values, include) => Object.fromEntries(Object.entries(values ?? {}).filter(([generation]) => generations.has(generation) === include));
            sessions.put({ ...legacy, key, bytes, epochs: pick(legacy.epochs, true), bindings: pick(legacy.bindings, true) });
            sessions.put({ ...legacy, bytes: legacy.bytes - bytes, epochs: pick(legacy.epochs, false), bindings: pick(legacy.bindings, false) });
            await done;
            return true;
        }
        catch (error) {
            try {
                transaction.abort();
            }
            catch { }
            await done.catch(() => undefined);
            throw error;
        }
    }
    /** The expected epoch, complete cut, and byte accounting commit in one transaction across tabs. */
    async replaceTimeline(scope, cut, options = {}) {
        const database = await this.open();
        const transaction = database.transaction(['sessions', 'events'], 'readwrite');
        const done = completed(transaction);
        void done.catch(() => undefined);
        const abort = () => { try {
            transaction.abort();
        }
        catch { /* A completed transaction needs no cancellation. */ } };
        options.signal?.addEventListener('abort', abort, { once: true });
        if (options.signal?.aborted)
            abort();
        const sessions = transaction.objectStore('sessions'), events = transaction.objectStore('events'), key = sessionKey(scope);
        try {
            const metas = await request(sessions.getAll());
            const meta = metas.find(value => value.key === key);
            if (!meta || meta.epochs?.[cut.generation] !== cut.previousEpoch) {
                const existing = await request(events.get(cut.id));
                if (meta?.epochs?.[cut.generation] === cut.epoch && existing?.sessionKey === key && (cut.digest ? existing.cut?.digest === cut.digest : JSON.stringify(existing.cut) === JSON.stringify(cut))) {
                    await done;
                    return;
                }
                throw new Error('stale_timeline_epoch');
            }
            if (cut.kind !== 'timeline_replacement' || cut.epoch === cut.previousEpoch || cut.records.some(record => record.generation !== cut.generation || record.frame.event.type !== 'timeline'))
                throw new Error('invalid_timeline_cut');
            const rows = await request(events.index('sessionKey').getAll(key));
            const removed = rows.filter(row => row.cut ? row.cut.generation === cut.generation : row.record.generation === cut.generation && row.record.frame.event.type === 'timeline' && !row.record.resolution);
            const bytes = new TextEncoder().encode(JSON.stringify(cut)).byteLength;
            const reclaimed = removed.reduce((sum, row) => sum + row.bytes, 0);
            if (metas.reduce((sum, value) => sum + value.bytes, 0) - reclaimed + bytes > this.maxBytes)
                throw new SessionCapacityError();
            for (const row of removed)
                events.delete(row.id);
            const order = Math.max(...metas.map(value => value.lastOrder ?? 0), 0) + 1;
            events.add({ id: cut.id, sessionKey: key, bytes, order, cut });
            meta.bytes += bytes - reclaimed;
            meta.epochs = { ...meta.epochs, [cut.generation]: cut.epoch };
            meta.lastOrder = order;
            meta.touched = Date.now();
            sessions.put(meta);
            await done;
        }
        catch (error) {
            abort();
            await done.catch(() => undefined);
            throw error;
        }
        finally {
            options.signal?.removeEventListener('abort', abort);
        }
    }
    async append(scope, records, changes = {}) {
        const database = await this.open();
        const transaction = database.transaction(['sessions', 'events'], 'readwrite');
        const done = completed(transaction);
        // Attach the rejection handler before issuing requests that could abort the transaction.
        void done.catch(() => undefined);
        const sessions = transaction.objectStore('sessions');
        const events = transaction.objectStore('events');
        const key = sessionKey(scope);
        const metas = await request(sessions.getAll());
        let nextOrder = metas.reduce((highest, item) => Math.max(highest, item.lastOrder ?? 0), 0);
        let meta = metas.find((item) => item.key === key);
        if (!meta) {
            meta = { key, bytes: 0, touched: 0, trimmed: false };
            metas.push(meta);
        }
        meta.touched = Date.now();
        meta.trimmed ||= changes.trimmed === true;
        const removedIds = [];
        for (const id of changes.removeIds ?? []) {
            const row = await request(events.get(id));
            if (row?.sessionKey !== key)
                continue;
            events.delete(id);
            meta.bytes -= row.bytes;
            removedIds.push(id);
        }
        for (const record of records) {
            const epoch = record.frame.timeline_epoch;
            const storedEpoch = meta.epochs?.[record.generation];
            const binding = record.frame.session_control_binding_id;
            if (epoch && binding && ['runtime_updated', 'agent_snapshot'].includes(record.frame.event.type)) {
                const previousBinding = meta.bindings?.[record.generation];
                if (previousBinding && previousBinding !== binding && storedEpoch && record.previousTimelineEpoch !== storedEpoch) {
                    removedIds.push(record.id);
                    continue;
                }
                if (previousBinding !== binding) {
                    meta.bindings = { ...meta.bindings, [record.generation]: binding };
                    meta.epochs = { ...meta.epochs, [record.generation]: epoch };
                }
            }
            if (record.frame.event.type === 'timeline' && storedEpoch && epoch !== storedEpoch) {
                removedIds.push(record.id);
                continue;
            }
            if (epoch && !storedEpoch && ['runtime_updated', 'agent_snapshot'].includes(record.frame.event.type))
                meta.epochs = { ...meta.epochs, [record.generation]: epoch };
            const existing = await request(events.get(record.id));
            if (existing) {
                if (!record.presentation || existing.sessionKey !== key)
                    continue;
                events.delete(record.id);
                meta.bytes -= existing.bytes;
            }
            const bytes = sessionRecordBytes(record);
            if (bytes > this.maxBytes) {
                transaction.abort();
                await done.catch(() => undefined);
                throw new SessionCapacityError();
            }
            await request(events.add({ id: record.id, sessionKey: key, bytes, order: ++nextOrder, record }));
            meta.bytes += bytes;
            meta.lastOrder = nextOrder;
            const event = record.resolution ?? record.frame.event;
            if (event.type === 'turn_started') {
                meta.active = true;
                meta.activeTask = record.frame.task_id;
            }
            else if (event.type === 'turn_completed' || event.type === 'turn_failed' || event.type === 'turn_canceled') {
                if (!meta.activeTask || meta.activeTask === record.frame.task_id) {
                    meta.active = false;
                    meta.activeTask = record.frame.task_id;
                }
            }
            else if ((event.type === 'timeline' || event.type === 'interaction_requested') && meta.activeTask !== record.frame.task_id) {
                meta.active = true;
                meta.activeTask = record.frame.task_id;
            }
        }
        let total = metas.reduce((sum, item) => sum + item.bytes, 0);
        const evictedKeys = [];
        const replacements = [];
        if (total > this.maxBytes) {
            const allRows = await request(events.getAll());
            for (const row of allRows.filter(row => row.cut).sort((a, b) => a.order - b.order)) {
                if (total <= this.maxBytes)
                    break;
                const owner = metas.find(value => value.key === row.sessionKey);
                if (!owner || owner.key === key || owner.active)
                    continue;
                events.delete(row.id);
                total -= row.bytes;
                owner.bytes -= row.bytes;
                owner.trimmed = true;
                if (!evictedKeys.includes(owner.key))
                    evictedKeys.push(owner.key);
            }
            const rows = allRows.filter((row) => !row.cut).sort((a, b) => a.order - b.order);
            const rowsById = new Map(rows.map(row => [row.id, row]));
            const units = metas.flatMap(owner => sessionRetentionUnits(rows.filter(row => row.sessionKey === owner.key).map(row => row.record))
                .map(unit => ({ ...unit, owner })));
            const removed = new Set();
            const remove = (record, owner) => {
                const row = rowsById.get(record.id);
                if (!row || removed.has(record.id))
                    return;
                removed.add(record.id);
                events.delete(record.id);
                total -= row.bytes;
                owner.bytes -= row.bytes;
                owner.trimmed = true;
                if (owner.key === key)
                    removedIds.push(record.id);
                else if (!evictedKeys.includes(owner.key))
                    evictedKeys.push(owner.key);
            };
            const buckets = new Map();
            for (const unit of units.filter(unit => !unit.protected)) {
                const oldTurn = unit.owner.key === key && unit.records.every(record => record.frame.task_id !== meta.activeTask);
                const rank = unit.owner.key !== key && !unit.owner.active ? 0 : oldTurn ? 1 : unit.owner.key !== key ? 2 : 3;
                const bucketKey = JSON.stringify([unit.owner.key, rank, rank === 1 ? sessionTurnKey(unit.records[0]) : rank === 3 ? unit.records[0].id : null]);
                const bucket = buckets.get(bucketKey) ?? { owner: unit.owner, records: [], rank };
                bucket.records.push(...unit.records);
                buckets.set(bucketKey, bucket);
            }
            const candidates = [...buckets.values()].sort((a, b) => a.rank - b.rank || a.owner.touched - b.owner.touched || a.records[0].ordinal - b.records[0].ordinal);
            for (const unit of candidates) {
                if (total <= this.maxBytes)
                    break;
                for (const record of unit.records)
                    remove(record, unit.owner);
            }
            if (total > this.maxBytes) {
                transaction.abort();
                await done.catch(() => undefined);
                throw new SessionCapacityError();
            }
            for (const owner of metas)
                sessions.put(owner);
        }
        sessions.put(meta);
        await done;
        return { trimmed: meta.trimmed, evictedKeys, removedIds, replacements };
    }
}
//# sourceMappingURL=indexeddb.js.map